import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';

import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DataTable } from '@/components/data-table/DataTable';
import { EditRowDialog, type EditField } from '@/components/data-table/EditRowDialog';
import { useTableState } from '@/hooks/useTableState';
import { schemaApi, type FieldInfo } from '@/api/schema';
import { tableOverrides, type ColumnOverride } from '@/config/table-overrides';
import { Skeleton } from '@/components/ui/skeleton';
import { DATE_LOCALE } from '@/lib/utils';

function getForeignKeyTables(
  tableName: string
): { fieldName: string; table: string; displayColumn: string; filterColumn: string }[] {
  const overrides = tableOverrides[tableName];
  if (!overrides?.columns) {
    return [];
  }
  const fks: { fieldName: string; table: string; displayColumn: string; filterColumn: string }[] = [];
  for (const [fieldName, col] of Object.entries(overrides.columns)) {
    if (col.foreignKey) {
      fks.push({ fieldName, ...col.foreignKey });
    }
  }
  return fks;
}

interface FiltersSearch {
  filters?: Record<string, string>;
}

export const Route = createFileRoute('/tables/$tableName')({
  component: DynamicTablePage,
  validateSearch: (search: Record<string, unknown>): FiltersSearch => {
    if (!search.filters) {
      return {};
    }
    if (typeof search.filters === 'object' && search.filters !== null) {
      return { filters: search.filters as Record<string, string> };
    }
    if (typeof search.filters === 'string') {
      try {
        return { filters: JSON.parse(search.filters) as Record<string, string> };
      } catch {
        return {};
      }
    }
    return {};
  }
});

function formatDate(val: unknown): string {
  if (!val) {
    return '—';
  }
  const d = new Date(val as string);
  if (isNaN(d.getTime())) {
    return String(val);
  }
  return d.toLocaleString(DATE_LOCALE, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}

function isDateField(type: string): boolean {
  return type === 'DateTime';
}

function CellWithTooltip({ value }: { value: string }) {
  if (value.length <= 50) {
    return <span>{value}</span>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="block truncate cursor-default">{value}</span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[400px] break-all">
        {value}
      </TooltipContent>
    </Tooltip>
  );
}

function buildColumns(
  fields: FieldInfo[],
  tableName: string,
  relatedData: Record<string, Record<string, string>>
): ColumnDef<Record<string, unknown>, unknown>[] {
  const overrides = tableOverrides[tableName];
  const columns: ColumnDef<Record<string, unknown>, unknown>[] = [];

  const hiddenSet = new Set(overrides?.hiddenColumns ?? []);

  // Inject virtual columns (defined on overrides via `via`) as synthetic scalar fields so the rest
  // of this function can treat them uniformly. The backend flattens them onto each row.
  const existingNames = new Set(fields.map(f => f.name));
  const virtualFields: FieldInfo[] = [];
  for (const [name, col] of Object.entries(overrides?.columns ?? {})) {
    if (!col.via || existingNames.has(name)) {
      continue;
    }
    virtualFields.push({
      name,
      type: 'Int',
      kind: 'scalar',
      isList: false,
      isRelation: false
    });
  }
  const allFields = [...fields, ...virtualFields];

  for (const field of allFields) {
    if (field.isRelation) {
      continue;
    }
    if (hiddenSet.has(field.name)) {
      continue;
    }

    const colOverride: ColumnOverride | undefined = overrides?.columns?.[field.name];

    if (colOverride?.hidden) {
      continue;
    }

    const col: ColumnDef<Record<string, unknown>, unknown> = {
      accessorKey: field.name,
      header: colOverride?.label ?? field.name,
      enableSorting: true,
      enableHiding: true
    };

    if (colOverride?.foreignKey) {
      const fk = colOverride.foreignKey;
      col.cell = ({ getValue }) => {
        const val = getValue();
        if (val == null) {
          return '—';
        }
        const displayValue =
          relatedData[fk.table]?.[String(val)] ?? (colOverride.format ? colOverride.format(val) : String(val));
        return (
          <Link
            to="/tables/$tableName"
            params={{ tableName: fk.table }}
            search={{ filters: { [fk.filterColumn]: String(val) } }}
            className="text-sidebar-primary hover:underline"
          >
            {displayValue}
          </Link>
        );
      };
    } else if (colOverride?.cell === 'image') {
      col.cell = ({ getValue }) => {
        const val = getValue();
        if (!val) {
          return '—';
        }
        return <img src={String(val)} alt="" className="size-8 rounded-md object-cover" />;
      };
    } else if (colOverride?.cell === 'boolean-badge') {
      col.cell = ({ getValue }) => {
        const val = getValue();
        return (
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
              val ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
            }`}
          >
            {val ? 'Yes' : 'No'}
          </span>
        );
      };
    } else if (colOverride?.cell === 'pnl') {
      const formatFn = colOverride.format;
      col.cell = ({ getValue }) => {
        const raw = getValue();
        const num = Number(raw);
        const display = formatFn ? formatFn(raw) : String(raw);
        return (
          <span className={num > 0 ? 'text-emerald-400' : num < 0 ? 'text-red-400' : 'text-muted-foreground'}>
            {display}
          </span>
        );
      };
    } else if (colOverride?.cell === 'side-badge') {
      col.cell = ({ getValue }) => {
        const val = String(getValue() ?? '');
        const lower = val.toLowerCase();
        const isPositive =
          lower === 'buy' || lower === 'matched' || lower === 'taker' || lower === 'live' || val === 'true';
        const isNegative = lower === 'sell' || lower === 'delayed' || lower === 'cancelled' || lower === 'false';
        return (
          <span
            className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
              isPositive
                ? 'bg-emerald-500/15 text-emerald-400'
                : isNegative
                  ? 'bg-red-500/15 text-red-400'
                  : 'bg-muted text-muted-foreground'
            }`}
          >
            {val}
          </span>
        );
      };
    } else if (colOverride?.link) {
      const linkFn = colOverride.link;
      const formatFn = colOverride.format;
      col.cell = ({ getValue, row }) => {
        const val = getValue();
        if (!val) {
          return '—';
        }
        const display = formatFn ? formatFn(val) : String(val);
        const href = linkFn(val, row.original as Record<string, unknown>);
        // linkFn may return an empty string when the row lacks the data needed to build a URL
        // (e.g. legacy order with no market_slug). In that case render plain text.
        if (!href) {
          return <CellWithTooltip value={display} />;
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" className="text-sidebar-primary hover:underline">
            {display}
          </a>
        );
      };
    } else if (colOverride?.format) {
      const formatFn = colOverride.format;
      col.cell = ({ getValue }) => {
        const val = getValue();
        if (val == null) {
          return '—';
        }
        return <CellWithTooltip value={formatFn(val)} />;
      };
    } else if (isDateField(field.type)) {
      col.cell = ({ getValue }) => {
        const val = getValue();
        return <CellWithTooltip value={formatDate(val)} />;
      };
    } else if (field.type === 'Boolean') {
      col.cell = ({ getValue }) => {
        const val = getValue();
        return <Badge variant={val ? 'default' : 'secondary'}>{val ? 'Yes' : 'No'}</Badge>;
      };
    } else {
      col.cell = ({ getValue }) => {
        const val = getValue();
        if (val == null) {
          return '—';
        }
        return <CellWithTooltip value={String(val)} />;
      };
    }

    columns.push(col);
  }

  // Add relation link columns for one-to-many relations
  for (const field of fields) {
    if (field.isList && field.isRelation) {
      const relatedTable = field.type.toLowerCase();

      columns.push({
        id: `_rel_${field.name}`,
        header: `View ${field.name}`,
        enableSorting: false,
        enableHiding: true,
        cell: ({ row }) => {
          const pk = row.original['id'];
          if (pk == null) {
            return null;
          }

          const targetFK = findForeignKeyColumn(fields, tableName, relatedTable);

          return (
            <Link
              to="/tables/$tableName"
              params={{ tableName: relatedTable }}
              search={{ filters: { [targetFK]: String(pk) } }}
              className="text-sidebar-primary hover:underline text-sm"
            >
              View {field.name}
            </Link>
          );
        }
      });
    }
  }

  return columns;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function findForeignKeyColumn(_fields: FieldInfo[], sourceTable: string, _targetTable: string): string {
  return `${sourceTable.replace(/s$/, '')}_id`;
}

const TABLE_LABELS: Record<string, string> = {
  agents: 'Agents',
  orders: 'Orders',
  trades: 'Trades',
  agents_performance_history: 'Performance History',
  llm_usage: 'LLM Usage'
};

function DynamicTablePage() {
  const { tableName } = Route.useParams();
  const { filters: urlFilters } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const tableState = useTableState({
    tableName,
    urlFilters,
    defaultSort: tableOverrides[tableName]?.defaultSort
      ? [{ id: tableOverrides[tableName]!.defaultSort!.id, desc: tableOverrides[tableName]!.defaultSort!.desc }]
      : undefined
  });

  const urlFiltersKey = urlFilters ? JSON.stringify(urlFilters) : '';
  useEffect(() => {
    if (urlFilters && Object.keys(urlFilters).length > 0) {
      tableState.setActiveFilters(urlFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFiltersKey]);

  const { data: schemaData, isLoading: schemaLoading } = useQuery({
    queryKey: ['schema'],
    queryFn: () => schemaApi.getSchema(),
    staleTime: Infinity
  });

  const model = useMemo(() => schemaData?.data?.find(m => m.name === tableName), [schemaData, tableName]);

  const fkTables = useMemo(() => getForeignKeyTables(tableName), [tableName]);

  const fkQueries = useQuery({
    queryKey: ['fk-lookup', tableName, fkTables.map(fk => fk.table).join(',')],
    queryFn: async () => {
      const results: Record<string, Record<string, string>> = {};
      await Promise.all(
        fkTables.map(async fk => {
          try {
            const resp = await schemaApi.getTableData(fk.table, { page: 1, pageSize: 1000 });
            const lookup: Record<string, string> = {};
            for (const row of resp.data.rows) {
              const key = String(row[fk.filterColumn] ?? '');
              const display = String(row[fk.displayColumn] ?? key);
              lookup[key] = display;
            }
            results[fk.table] = lookup;
          } catch {
            // Silently ignore - will fall back to showing IDs
          }
        })
      );
      return results;
    },
    enabled: fkTables.length > 0,
    staleTime: 5 * 60 * 1000
  });

  const relatedData = useMemo(() => fkQueries.data ?? {}, [fkQueries.data]);

  const sortBy = tableState.sorting[0]?.id;
  const sortOrder = tableState.sorting[0]?.desc === false ? 'asc' : 'desc';

  // Translate foreign-key filters from "name substring" to comma-separated matching IDs,
  // so typing "insider-gemini" in the Agent column actually matches the right agent_id rows.
  const resolvedFilters = useMemo(() => {
    const fkMap = new Map(fkTables.map(fk => [fk.fieldName, fk]));
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(tableState.activeFilters)) {
      const fk = fkMap.get(key);
      if (fk && value && !/^-?\d+(,-?\d+)*$/.test(value)) {
        const lookup = relatedData[fk.table] ?? {};
        const q = value.toLowerCase();
        const matchedIds = Object.entries(lookup)
          .filter(([, display]) => display.toLowerCase().includes(q))
          .map(([id]) => id);
        result[key] = matchedIds.length > 0 ? matchedIds.join(',') : '-1';
      } else {
        result[key] = value;
      }
    }
    return result;
  }, [tableState.activeFilters, fkTables, relatedData]);

  const { data: tableData, isLoading: dataLoading } = useQuery({
    queryKey: ['table', tableName, tableState.page, tableState.pageSize, sortBy, sortOrder, resolvedFilters],
    queryFn: () =>
      schemaApi.getTableData(tableName, {
        page: tableState.page,
        pageSize: tableState.pageSize,
        sortBy,
        sortOrder: sortOrder as 'asc' | 'desc',
        filters: resolvedFilters
      }),
    enabled: !!model
  });

  const columns = useMemo(
    () => (model ? buildColumns(model.fields, tableName, relatedData) : []),
    [model, tableName, relatedData]
  );

  const dateColumns = useMemo(() => {
    if (!model) {
      return new Set<string>();
    }
    return new Set(model.fields.filter(f => !f.isRelation && f.type === 'DateTime').map(f => f.name));
  }, [model]);

  const booleanColumns = useMemo(() => {
    if (!model) {
      return new Set<string>();
    }
    return new Set(model.fields.filter(f => !f.isRelation && f.type === 'Boolean').map(f => f.name));
  }, [model]);

  const fkColumns = useMemo(() => {
    const map: Record<string, string> = {};
    for (const fk of fkTables) {
      map[fk.fieldName] = fk.table;
    }
    return map;
  }, [fkTables]);

  const editFields: EditField[] = useMemo(() => {
    if (!model) {
      return [];
    }
    const overrides = tableOverrides[tableName];

    return model.fields
      .filter(f => !f.isRelation && !f.isList)
      .map(f => {
        const colOverride = overrides?.columns?.[f.name];
        const label = colOverride?.label ?? f.name;
        const isId = f.name === 'id';
        const isCreatedAt = f.name === 'created_at' || f.name === 'createdAt';

        let fieldType: EditField['type'] = 'string';
        if (f.type === 'Int' || f.type === 'Float' || f.type === 'BigInt') {
          fieldType = 'number';
        } else if (f.type === 'Decimal') {
          fieldType = 'decimal';
        } else if (f.type === 'Boolean') {
          fieldType = 'boolean';
        } else if (f.type === 'DateTime') {
          fieldType = 'datetime';
        } else if (f.name === 'description' || f.name === 'comment') {
          fieldType = 'text';
        }

        return {
          name: f.name,
          label,
          type: fieldType,
          readonly: isId || isCreatedAt
        };
      });
  }, [model, tableName]);

  const [editRow, setEditRow] = useState<Record<string, unknown> | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (ids: (string | number)[]) => schemaApi.deleteRows(tableName, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['table', tableName] });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: Record<string, unknown> }) =>
      schemaApi.updateRow(tableName, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['table', tableName] });
      setEditOpen(false);
      setEditRow(null);
    }
  });

  const handleEditRow = (row: Record<string, unknown>) => {
    setEditRow(row);
    setEditOpen(true);
  };

  const handleSaveRow = (changes: Record<string, unknown>) => {
    if (!editRow) {
      return;
    }
    const id = editRow.id ?? editRow.name;
    if (id == null) {
      return;
    }
    updateMutation.mutate({ id: id as string | number, data: changes });
  };

  const handleDeleteSelected = (ids: (string | number)[]) => {
    if (confirm(`Are you sure you want to delete ${ids.length} row(s)?`)) {
      deleteMutation.mutate(ids);
    }
  };

  const handleActiveFiltersChange = (filters: Record<string, string>) => {
    tableState.setActiveFilters(filters);
    tableState.setPage(1);
  };

  const handleClearFilters = () => {
    tableState.clearFilters();
    navigate({
      to: '/tables/$tableName',
      params: { tableName },
      search: {}
    });
  };

  if (schemaLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (!model) {
    return <div className="py-12 text-center text-muted-foreground">Table &quot;{tableName}&quot; not found</div>;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <DataTable
        title={TABLE_LABELS[tableName] ?? tableName}
        columns={columns}
        data={tableData?.data.rows ?? []}
        totalCount={tableData?.data.totalCount ?? 0}
        page={tableState.page}
        pageSize={tableState.pageSize}
        sorting={tableState.sorting}
        columnVisibility={tableState.columnVisibility}
        columnOrder={tableState.columnOrder}
        columnSizing={tableState.columnSizing}
        activeFilters={tableState.activeFilters}
        onSortingChange={tableState.setSorting}
        onColumnVisibilityChange={tableState.setColumnVisibility}
        onColumnOrderChange={tableState.setColumnOrder}
        onColumnSizingChange={tableState.setColumnSizing}
        onPageChange={tableState.setPage}
        onPageSizeChange={tableState.setPageSize}
        onActiveFiltersChange={handleActiveFiltersChange}
        onClearFilters={handleClearFilters}
        isLoading={dataLoading}
        onDeleteSelected={handleDeleteSelected}
        onEditRow={handleEditRow}
        dateColumns={dateColumns}
        booleanColumns={booleanColumns}
        fkColumns={fkColumns}
        fkLookups={relatedData}
        getRowId={(row: Record<string, unknown>) => String(row.id ?? row.name ?? '')}
      />
      <EditRowDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        fields={editFields}
        row={editRow}
        onSave={handleSaveRow}
        isSaving={updateMutation.isPending}
        title={`Edit ${TABLE_LABELS[tableName] ?? tableName}`}
      />
    </div>
  );
}
